import {Component, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {UserService} from '@services/user.service';
import {TrackingService} from '@services/tracking.service';
import {AlertService} from '@services/alert.service';
import {HttpClient} from '@angular/common/http';
import {environment} from '~/environments/environment';

@Component({
    selector: 'app-churn',
    templateUrl: './churn.component.html',
    styleUrls: ['./churn.component.scss']
})
export class ChurnComponent {

    step = 'reason';
    selectedReason = '';
    feedback = '';
    loading = false;

    reasons = [
        {key: 'A', label: 'Les outils ne fonctionnent pas correctement'},
        {key: 'B', label: 'Il manque des fonctionnalités'},
        {key: 'C', label: 'J\'ai trouvé un bien à acheter'},
        {key: 'D', label: 'J\'ai abandonné mon projet immobilier'},
        {key: 'E', label: 'J\'ai trouvé un autre service qui me convient mieux'},
        {key: 'F', label: 'Je n\'ai pas le budget'},
        {key: 'G', label: 'Je veux changer de forfait'},
        {key: 'H', label: 'Autre'},
    ];

    constructor(
        public dialogRef: MatDialogRef<ChurnComponent>,
        private http: HttpClient,
        private userService: UserService,
        private trackingService: TrackingService,
        private alertService: AlertService,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {
    }

    get title(): string {
        switch (this.step) {
            case 'reason':
                return 'Pourquoi voulez-vous résilier ?';
            case 'offer-bug':
            case 'offer-discount':
                return 'Offre spéciale';
            case 'congrats':
                return 'Félicitations !';
            case 'feedback-service':
            case 'feedback-improve':
                return 'Votre feedback';
            case 'confirmation':
                return 'Confirmation';
            case 'cancelled':
                return 'Résiliation validée';
            case 'offer-accepted':
                return 'Offre acceptée';
            default:
                return '';
        }
    }

    get titleClass(): string {
        if (this.step === 'offer-accepted' || this.step === 'cancelled') {
            return 'success';
        }
        return '';
    }

    private get churnData(): any {
        const reason = this.reasons.find(r => r.key === this.selectedReason)?.label || '';
        return {
            reason,
            feedback: this.feedback,
        };
    }

    onNoClick(): void {
        this.dialogRef.close(false);
    }

    selectReason(key: string): void {
        this.selectedReason = key;
    }

    // Workflows:
    // A -> 1(offer-bug) -> accept=8 | decline=6
    // B -> 5(feedback-improve) -> 3(offer-discount) -> accept=8 | decline=6
    // C -> 2(congrats) -> accept=change-plan | decline=6
    // D -> 3(offer-discount) -> accept=8 | decline=6
    // E -> 4(feedback-service) -> 3(offer-discount) -> accept=8 | decline=6
    // F -> 3(offer-discount) -> accept=8 | decline=6
    // G -> change-plan
    // H -> 5(feedback-improve) -> 3(offer-discount) -> accept=8 | decline=6

    nextFromReason(): void {
        if (!this.selectedReason) return;

        switch (this.selectedReason) {
            case 'A':
                this.step = 'offer-bug';
                break;
            case 'B':
            case 'H':
                this.step = 'feedback-improve';
                break;
            case 'C':
                this.step = 'congrats';
                break;
            case 'D':
            case 'F':
                this.step = 'offer-discount';
                break;
            case 'E':
                this.step = 'feedback-service';
                break;
            case 'G':
                this.dialogRef.close('change-plan');
                break;
        }
    }

    nextFromFeedbackService(): void {
        this.step = 'offer-discount';
    }

    nextFromFeedbackImprove(): void {
        this.step = 'offer-discount';
    }

    // Popup 8 : accept offer -> envoie les données + action accept
    acceptOffer(): void {
        if (this.loading) return;
        this.loading = true;

        this.http.post<any>(`${environment.apiUrl}app/user/plan`, {
            ...this.churnData,
        }).subscribe(
            () => {
                this.loading = false;
                this.step = 'offer-accepted';
            },
            () => {
                this.loading = false;
                this.alertService.error('Une erreur est survenue, veuillez réessayer.');
            }
        );
    }

    declineOffer(): void {
        this.step = 'confirmation';
    }

    goToChangePlan(): void {
        this.dialogRef.close('change-plan');
    }

    goBack(): void {
        switch (this.step) {
            case 'offer-bug':
            case 'congrats':
                this.step = 'reason';
                break;
            case 'offer-discount':
                if (this.selectedReason === 'B' || this.selectedReason === 'H') {
                    this.step = 'feedback-improve';
                } else if (this.selectedReason === 'E') {
                    this.step = 'feedback-service';
                } else {
                    this.step = 'reason';
                }
                break;
            case 'feedback-service':
            case 'feedback-improve':
                this.step = 'reason';
                break;
            case 'confirmation':
                if (this.selectedReason === 'A') {
                    this.step = 'offer-bug';
                } else if (this.selectedReason === 'C') {
                    this.step = 'congrats';
                } else {
                    this.step = 'offer-discount';
                }
                break;
        }
    }

    // Popup 7 : cancel -> DELETE avec les données churn
    confirmCancel(): void {
        if (this.loading) return;
        this.loading = true;

        let churnData = {
            ...this.churnData,
        };

        this.trackingService.cancel(churnData.feedback, churnData.reason);
        this.userService.cancelPlan(churnData).subscribe(
            () => {
                this.loading = false;
                this.step = 'cancelled';
            },
            () => {
                this.loading = false;
                this.alertService.error('Une erreur est survenue, veuillez réessayer ou nous contacter si le problème persiste.');
            }
        );
    }

    openCalendar(): void {
        window.open('https://calendar.google.com/calendar/appointments/schedules/AcZssZ37vqkpu4wgVerqnEbxg8CMz1qHAfFxtzxNHDNtNe5VC09lbTI-KSRRIPT04XZ8ll21KnhaUW44', '_blank');
        this.dialogRef.close(true);
    }
}
